import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  NONE: '',
  USD: '$', EUR: '€', GBP: '£', JPY: '¥',
  CAD: 'C$', AUD: 'A$', NZD: 'NZ$', HKD: 'HK$', SGD: 'S$', TWD: 'NT$', MXN: 'MX$',
  CHF: 'Fr', SEK: 'kr', NOK: 'kr', DKK: 'kr',
  PLN: 'zł', CZK: 'Kč', HUF: 'Ft', RON: 'lei', BGN: 'лв',
  CNY: 'CN¥', KRW: '₩', INR: '₹', THB: '฿', IDR: 'Rp', MYR: 'RM', PHP: '₱',
  VND: '₫', PKR: '₨', BDT: '৳',
  TRY: '₺', ILS: '₪', RUB: '₽',
  BRL: 'R$', ZAR: 'R', NGN: '₦', KES: 'KSh', EGP: 'E£',
  AED: 'د.إ', SAR: '﷼', QAR: 'ر.ق', KWD: 'د.ك',
  ARS: 'AR$', CLP: 'CL$', COP: 'Col$', PEN: 'S/',
};

/** Return the display symbol for any ISO 4217 currency code (e.g. "JPY" → "¥", "AUD" → "A$"). */
export function getCurrencySymbol(code: string): string {
  if (!code) return "$";
  if (code in CURRENCY_SYMBOLS) return CURRENCY_SYMBOLS[code];
  try {
    const parts = new Intl.NumberFormat('en', { style: 'currency', currency: code, minimumFractionDigits: 0 })
      .formatToParts(0);
    return parts.find(p => p.type === 'currency')?.value ?? code;
  } catch {
    return code;
  }
}

/** Convert ISO date string "YYYY-MM-DD" → display format.
 * Defaults to MM/DD/YYYY; uses DD/MM/YYYY for GBP/EUR unless override says otherwise. */
export function formatDate(iso: string, currency?: string, dateFormatOverride?: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  const useEU = dateFormatOverride === 'eu' ||
    (dateFormatOverride !== 'us' && (currency === 'GBP' || currency === 'EUR'));
  return useEU ? `${d}/${m}/${y}` : `${m}/${d}/${y}`;
}

/** Short display format (no year): MM/DD or DD/MM depending on locale. Pass includeYear for MM/DD/YY variant. */
export function formatDateShort(iso: string, currency?: string, dateFormatOverride?: string, includeYear?: boolean): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!m || !d) return iso;
  const useEU = dateFormatOverride === 'eu' ||
    (dateFormatOverride !== 'us' && (currency === 'GBP' || currency === 'EUR'));
  if (includeYear && y) {
    const yy = y.slice(2);
    return useEU ? `${d}/${m}/${yy}` : `${m}/${d}/${yy}`;
  }
  return useEU ? `${d}/${m}` : `${m}/${d}`;
}
